import React, { useEffect, useState } from 'react';
import PhotoGalleryModal from './PhotoGalleryModal';
import { trackStepCompletion, trackConfigurationChange, startStepTimer, trackStepTiming } from '../services/analytics';
import { getStartingPricesByShadeType } from '../services/productService';

const SHADE_TYPES = {
  'pleated': {
    name: 'Pleated',
    description: 'Classic pleated shades with sheer fabric. USA Custom Made.',

    image: 'https://ervp-spa.s3.us-east-1.amazonaws.com/shade-builder/pleated.png'
  },
  'valu-line': {
    name: 'Valu-Line Roller',
    description: 'Budget-friendly roller shades, 3-ply blackout. USA Custom Made.',

    image: 'https://ervp-spa.s3.us-east-1.amazonaws.com/shade-builder/valuline.png'
  },
  'premium': {
    name: 'Premium Roller',
    description: 'Slow-Rise roller shades, 4-ply blackout. USA Custom Made.',

    image: 'https://ervp-spa.s3.us-east-1.amazonaws.com/shade-builder/premium.png'
  }
};

function ShadeTypeSelector({ selectedType, shadeConfiguration, onUpdate }) {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryShadeType, setGalleryShadeType] = useState(null);
  const [startingPrices, setStartingPrices] = useState({});

  useEffect(() => {
    let active = true;
    getStartingPricesByShadeType()
      .then(prices => { if (active) setStartingPrices(prices); })
      .catch(error => console.error('Unable to load live starting prices:', error));
    return () => { active = false; };
  }, []);

  const handleTypeSelect = (type) => {
    if (selectedType !== type) {
      trackConfigurationChange('shade_type', selectedType, type, 1);
    }
    onUpdate({ selectedType: type });
  };

  const handleConfigurationSelect = (config) => {
    if (shadeConfiguration !== config) {
      trackConfigurationChange('shade_configuration', shadeConfiguration, config, 1);
    }
    onUpdate({ shadeConfiguration: config });
    
    // Track step 1 completion when both type and configuration are selected
    if (selectedType && config) {
      trackStepTiming(1);
      trackStepCompletion(1, {
        shade_type: selectedType,
        configuration: config,
        shade_name: SHADE_TYPES[selectedType].name,
        base_price: startingPrices[selectedType] || null
      });
      startStepTimer(2);
    }
  };

  const openGallery = (type, event) => {
    event.stopPropagation();
    setGalleryShadeType(type);
    setGalleryOpen(true);
  };

  return (
    <div>
      {/* Shade Type Selection */}
      <div className="tw-mb-8">
        <div className="tw-grid tw-grid-cols-3 md:tw-grid-cols-3 tw-gap-4">
          {Object.entries(SHADE_TYPES).map(([key, type]) => (
            <button
              key={key}
              onClick={() => handleTypeSelect(key)}
              className={`tw-p-4 tw-border-2 tw-rounded-lg tw-transition-all tw-text-left tw-relative
                ${selectedType === key 
                  ? 'tw-border-brand-red tw-bg-red-50' 
                  : 'tw-border-gray-300 hover:tw-border-gray-400 tw-bg-white'}`}
            >
              <img 
                src={type.image} 
                alt={type.name}
                className="tw-w-full tw-h-32 tw-object-contain tw-rounded tw-mb-3"
              />
              <h4 className="tw-font-semibold tw-text-lg tw-mb-1">{type.name}</h4>
              <p className="tw-text-sm tw-text-gray-600 tw-mb-2 tw-hidden md:tw-block">{type.description}</p>
              <p className="tw-text-sm tw-font-medium">
                {startingPrices[key]
                  ? `From $${startingPrices[key].toFixed(2)}`
                  : 'Loading live price…'}
              </p>
              
              {/* Photo Gallery Button */}
              <button
                onClick={(e) => openGallery(key, e)}
                className="tw-mt-3 tw-text-sm tw-text-brand-red hover:tw-text-red-700 tw-font-medium tw-flex tw-items-center"
              >
                <svg className="tw-w-4 tw-h-4 tw-mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Gallery
              </button>
            </button>
          ))}
        </div>
      </div>

      {/* Configuration Selection */}
      {selectedType && (
        <div className="tw-animate-fade-in">
          <h3 className="tw-text-lg tw-font-medium tw-mb-4">Select Configuration</h3>
          <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-2 tw-gap-4">
            <button
              onClick={() => handleConfigurationSelect('day-night')}
              className={`tw-p-6 tw-border-2 tw-rounded-lg tw-transition-all
                ${shadeConfiguration === 'day-night' 
                  ? 'tw-border-brand-red tw-bg-red-50' 
                  : 'tw-border-gray-300 hover:tw-border-gray-400 tw-bg-white'}`}
            >
              <div className="tw-flex tw-items-center tw-mb-3">
                <svg className="tw-w-8 tw-h-8 tw-mr-3 tw-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
                </svg>
                <h4 className="tw-font-semibold tw-text-lg">Day & Night</h4>
              </div>
              <p className="tw-text-sm tw-text-gray-600 tw-hidden md:tw-block">
                Dual shade system with both light-filtering day shade and blackout night shade. 
                Perfect for complete light control.
              </p>
            </button>

            <button
              onClick={() => handleConfigurationSelect('night')}
              className={`tw-p-6 tw-border-2 tw-rounded-lg tw-transition-all
                ${shadeConfiguration === 'night' 
                  ? 'tw-border-brand-red tw-bg-red-50' 
                  : 'tw-border-gray-300 hover:tw-border-gray-400 tw-bg-white'}`}
            >
              <div className="tw-flex tw-items-center tw-mb-3">
                <svg className="tw-w-8 tw-h-8 tw-mr-3 tw-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
                <h4 className="tw-font-semibold tw-text-lg">Night Only</h4>
              </div>
              <p className="tw-text-sm tw-text-gray-600 tw-hidden md:tw-block">
                Single blackout shade for privacy and complete darkness. 
                Ideal for bedrooms and sleeping areas.
              </p>
            </button>
          </div>
        </div>
      )}

      {/* Photo Gallery Modal */}
      <PhotoGalleryModal
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        shadeType={galleryShadeType}
        shadeName={galleryShadeType ? SHADE_TYPES[galleryShadeType].name : ''}
      />
    </div>
  );
}

export default ShadeTypeSelector;